/**
 * The dashboard's session state.
 *
 * Four states and three actions, and the interesting thing is what is held where.
 *
 * **Nothing durable.** No `localStorage`, no `sessionStorage`, no cookie this code writes. The
 * credential is an `httpOnly` cookie the browser holds and script cannot read; the role and the CSRF
 * token live in this hook's state, which is gone on reload. That is the direct fix for the defect
 * requirement 5.2 names: the dashboard being replaced writes its JWT and the whole user record to
 * `localStorage` and reads the role back out of it in `header.tsx`, where any injected script can
 * read both.
 *
 * **The role is for drawing, and that is all.** `session.user.rol` decides which buttons this app
 * renders. It decides nothing about what the API will do: every protected route re-derives the role
 * from the session row on the server (`requireRole`), so a user who edits this value in a debugger
 * gets a differently-drawn page and the same 403.
 *
 * **A reload recovers by asking.** `GET /api/auth/session` is the first request the app makes: the
 * cookie is still in the browser, so the server can say who the caller is and hand back a fresh CSRF
 * token. There is nothing to restore from disk because nothing was written to disk.
 */
import {
  authSessionSchema,
  csrfTokenSchema,
  revocationSchema,
  type AuthSession,
} from '@apuestapr/contracts';
import { useCallback, useEffect, useState } from 'react';

import type { ApiClient, ApiFailure, ApiResult } from '../api/client.ts';

export const csrfPath = '/api/auth/csrf';
export const loginPath = '/api/auth/login';
export const logoutPath = '/api/auth/logout';
export const sessionPath = '/api/auth/session';

export type SessionState =
  /** The first `GET /api/auth/session` is in flight. */
  | { readonly status: 'checking' }
  /** No session, or the one we had is gone. `failure` is present only when something went wrong. */
  | { readonly status: 'anonymous'; readonly failure?: ApiFailure }
  | { readonly status: 'authenticated'; readonly session: AuthSession }
  /** A login attempt failed, or an authenticated request did. */
  | { readonly status: 'error'; readonly failure: ApiFailure };

export interface SessionApi {
  readonly state: SessionState;
  readonly logIn: (email: string, password: string) => Promise<void>;
  readonly logOut: () => Promise<void>;
  /** Re-ask the server. What the demo uses to show a revoked session failing. */
  readonly refresh: () => Promise<void>;
}

/** A 401 is not an error state — it is the answer "you are not logged in". */
const isUnauthenticated = (failure: ApiFailure): boolean =>
  failure.kind === 'http' && failure.status === 401;

export const useSession = (client: ApiClient): SessionApi => {
  const [state, setState] = useState<SessionState>({ status: 'checking' });

  /** What `GET /api/auth/session` means, in one place, so the mount and the button agree. */
  const stateFrom = (result: ApiResult<AuthSession>): SessionState => {
    if (result.ok) return { status: 'authenticated', session: result.data };
    // A 401 is not an error state — it is the answer "you are not logged in", and the form is the
    // right thing to render for it.
    return isUnauthenticated(result.failure)
      ? { status: 'anonymous' }
      : { status: 'anonymous', failure: result.failure };
  };

  const refresh = useCallback(async (): Promise<void> => {
    setState(stateFrom(await client.get(sessionPath, authSessionSchema)));
  }, [client]);

  useEffect(() => {
    let active = true;

    // The first request the app makes. The cookie is already in the browser — it is `httpOnly`, so
    // this code cannot see it — and the server is what says who the caller is. Nothing is restored
    // from storage, because nothing was written there.
    void (async () => {
      const result = await client.get(sessionPath, authSessionSchema);
      if (!active) return;
      setState(stateFrom(result));
    })();

    return () => {
      active = false;
    };
  }, [client]);

  const logIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      // Two requests, because login is not exempt from the CSRF check — a cross-site form post that
      // logs a staff user into an attacker's account is the interesting case to exempt.
      const primed = await client.get(csrfPath, csrfTokenSchema);
      if (!primed.ok) {
        setState({ status: 'error', failure: primed.failure });
        return;
      }

      const result = await client.send('POST', loginPath, authSessionSchema, {
        body: { email, password },
        csrfToken: primed.data.csrfToken,
      });

      setState(
        result.ok
          ? { status: 'authenticated', session: result.data }
          : { status: 'error', failure: result.failure },
      );
    },
    [client],
  );

  const logOut = useCallback(async (): Promise<void> => {
    const csrfToken = state.status === 'authenticated' ? state.session.csrfToken : undefined;
    const result = await client.send('POST', logoutPath, revocationSchema, { csrfToken });

    // Anonymous either way. A logout that failed on the wire has still lost this tab's token, and
    // leaving the user on a screen that claims they are signed in would be the worse lie.
    setState(
      result.ok || isUnauthenticated(result.failure)
        ? { status: 'anonymous' }
        : { status: 'anonymous', failure: result.failure },
    );
  }, [client, state]);

  return { state, logIn, logOut, refresh };
};
